import styled, { css } from 'styled-components';
import { FiShield, FiAlertCircle, FiCheck } from 'react-icons/fi';

const MOBILE = '@media (max-width: 768px)';

export const OtpRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: space-between;
  margin: 4px 0 8px;

  ${MOBILE} {
    gap: 6px;
  }
`;

export const OtpCell = styled.input`
  flex: 1;
  min-width: 0;
  max-width: 48px;
  height: 56px;
  text-align: center;
  font-size: 22px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.semantic.text.primary};
  background: ${({ theme }) => theme.colors.semantic.surface.card};
  border: 2px solid
    ${({ theme, $error, $filled }) => {
      if ($error) return theme.colors.danger500;
      if ($filled) return theme.colors.primary500;
      return theme.colors.semantic.border.default;
    }};
  border-radius: 8px;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    border-color: ${({ theme, $error }) =>
      $error ? theme.colors.danger500 : theme.colors.primary500};
    box-shadow: 0 0 0 3px
      ${({ theme, $error }) =>
        $error ? theme.colors.danger50 : theme.colors.primary50};
  }

  ${MOBILE} {
    height: 48px;
    font-size: 18px;
  }
`;

export const Countdown = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;
  margin-bottom: 16px;
  padding: 4px 12px;
  border-radius: 100px;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  border: 1px solid
    ${({ theme, $warn }) =>
      $warn ? theme.colors.warning500 : theme.colors.primary200};
  background: ${({ theme, $warn }) =>
    $warn ? theme.colors.warning50 : theme.colors.primary50};
  color: ${({ theme, $warn }) =>
    $warn ? theme.colors.warning500 : theme.colors.primary700};
`;

export const CountdownDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
`;

export const Feedback = styled.p`
  margin: 0 0 8px;
  font-size: 14px;
  line-height: 1.4;
  color: ${({ theme, $error }) =>
    $error ? theme.colors.danger500 : theme.colors.semantic.text.secondary};
`;

export const Banner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  margin-bottom: 20px;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.danger500};
  background: ${({ theme }) => theme.colors.semantic.status.danger};
  color: ${({ theme }) => theme.colors.semantic.text.primary};
  font-size: 14px;
  line-height: 1.4;
`;

export const BannerIcon = styled(FiAlertCircle)`
  flex-shrink: 0;
  margin-top: 2px;
  color: ${({ theme }) => theme.colors.danger500};
`;

export const QrFrame = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: fit-content;
  margin: 0 auto;
  ${({ $spaced }) => $spaced && 'margin-bottom: 12px;'}
  padding: 6px;
  background: ${({ theme }) => theme.colors.semantic.surface.card};
  border: 1px solid ${({ theme }) => theme.colors.semantic.border.default};
  border-radius: 8px;

  ${MOBILE} {
    canvas,
    svg {
      width: 140px;
      height: 140px;
    }
  }
`;

export const StepHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
`;

export const ProgressTrack = styled.div`
  display: flex;
  gap: 5px;
  ${({ $grow }) =>
    $grow &&
    css`
      flex: 1;
    `}
`;

export const ProgressSegment = styled.div`
  height: 4px;
  border-radius: 2px;
  ${({ $grow }) => ($grow ? 'flex: 1;' : 'width: 36px;')}
  background: ${({ theme, $done }) =>
    $done ? theme.colors.primary500 : theme.colors.semantic.border.default};
`;

export const StepLabel = styled.span`
  flex: none;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.semantic.text.placeholder};
`;

export const ProgressGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.semantic.text.secondary};
`;

export const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
`;

export const AppChip = styled.button`
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.primary200 : theme.colors.semantic.border.subtle};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primary50 : 'transparent'};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.primary700 : theme.colors.semantic.text.primary};

  &:hover {
    border: 1px solid ${({ theme }) => theme.colors.primary700};
    color: ${({ theme }) => theme.colors.primary900};
  }
`;

export const QrPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
  padding: 8px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.semantic.surface.muted};
  border: 1px solid ${({ theme }) => theme.colors.semantic.border.subtle};
`;

export const QrCaption = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.4;
  text-align: center;
  color: ${({ theme }) => theme.colors.semantic.text.secondary};
`;

export const StoreButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
`;

export const StoreButton = styled.a`
  display: flex;
  flex: 1 1 0;
  min-width: 0;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 44px;
  padding: 6px 8px;
  line-height: 1.2;
  text-align: center;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.semantic.text.primary};
  background: ${({ theme }) => theme.colors.semantic.surface.card};
  border: 1px solid ${({ theme }) => theme.colors.semantic.border.default};

  svg {
    flex: none;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary200};
  }
`;

export const Secret = styled.div`
  font-family: 'Courier New', Courier, monospace;
  font-size: 16px;
  letter-spacing: 2px;
  text-align: center;
  word-break: break-all;
  margin-bottom: 16px;
  padding: 8px;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.semantic.text.primary};
  background: ${({ theme }) => theme.colors.primary50};
  border: 1px dashed ${({ theme }) => theme.colors.primary200};
`;

export const IconBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  align-self: center;
  margin-bottom: 16px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary50};
  border: 2px solid ${({ theme }) => theme.colors.primary200};
  justify-self: center;
`;

export const ShieldIcon = styled(FiShield)`
  color: ${({ theme }) => theme.colors.primary500};
`;

export const LostAccessBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 8px 0 4px;
`;

export const LostAccessText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  text-align: center;
  color: ${({ theme }) => theme.colors.semantic.text.secondary};
`;

export const CheckIcon = styled(FiCheck)`
  color: ${({ theme }) => theme.colors.primary500};
`;

export const Centered = styled.div`
  text-align: center;
`;

export const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

export const TutorialTrigger = styled.button`
  align-self: flex-start;
  margin-bottom: 16px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary700};
  text-decoration: underline;
`;

export const TutorialSteps = styled.ol`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
  counter-reset: tutorial;
`;

export const TutorialStep = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 14px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.semantic.text.primary};
  counter-increment: tutorial;

  &::before {
    content: counter(tutorial);
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    font-size: 12px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.primary700};
    background: ${({ theme }) => theme.colors.primary50};
    border: 1px solid ${({ theme }) => theme.colors.primary200};
  }
`;

export const StepText = styled.span`
  flex: 1;
  padding-top: 2px;
`;

export const CopyField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin-bottom: 12px;
`;

export const CopyValue = styled.code`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 8px;
  font-family: 'Courier New', Courier, monospace;
  font-size: 14px;
  letter-spacing: 1px;
  word-break: break-all;
  color: ${({ theme }) => theme.colors.semantic.text.primary};
  background: ${({ theme }) => theme.colors.primary50};
  border: 1px dashed ${({ theme }) => theme.colors.primary200};
`;

export const CopyButton = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease, border-color 0.15s ease;
  color: ${({ theme, $copied }) =>
    $copied ? theme.colors.success500 : theme.colors.primary700};
  background: ${({ theme, $copied }) =>
    $copied ? theme.colors.success50 : theme.colors.primary50};
  border: 1px solid
    ${({ theme, $copied }) =>
      $copied ? theme.colors.success500 : theme.colors.primary200};

  &:hover {
    border-color: ${({ theme, $copied }) =>
      $copied ? theme.colors.success500 : theme.colors.primary500};
  }
`;

export const StepCopy = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

export const CopyHint = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.semantic.text.secondary};
`;

export const CopyTextButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  transition: color 0.15s ease, background 0.15s ease, border-color 0.15s ease;
  color: ${({ theme, $copied }) =>
    $copied ? theme.colors.success500 : theme.colors.primary700};
  background: ${({ theme, $copied }) =>
    $copied ? theme.colors.success50 : theme.colors.primary50};
  border: 1px solid
    ${({ theme, $copied }) =>
      $copied ? theme.colors.success500 : theme.colors.primary200};

  &:hover {
    border-color: ${({ theme, $copied }) =>
      $copied ? theme.colors.success500 : theme.colors.primary500};
  }
`;

export const TutorialMedia = styled.div`
  margin-top: 16px;

  img {
    display: block;
    width: 100%;
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.colors.semantic.border.default};
  }
`;

export const HelpTitle = styled.p`
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary700};
`;

export const HelpBlock = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
`;

export const HelpText = styled.p`
  margin: 0 0 12px;
  font-size: 14px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.semantic.text.secondary};
`;
